/**
 * GlobalTools Index
 *
 * Main entry point for the GlobalTools framework.
 * Exports all tools, the registry, runtime, and utilities.
 */

// ============================================================================
// Imports (must be at top for ESLint import/first rule)
// ============================================================================

import { registerTool } from './registry';
import { documentAnalyzerTool } from './tools/documentAnalyzer';
import { multiDocManifestTool } from './tools/multiDocManifest';
import { translateTool, BUTTON_IDS as TRANSLATE_BUTTON_IDS } from './tools/translate';
import { videoScriptTool, BUTTON_IDS as VIDEO_SCRIPT_BUTTON_IDS } from './tools/videoScript';
import { deepResearchTool } from './tools/deepResearch';
import { paymentGateTool } from './tools/paymentGate';
import { legalDocCreatorTool } from './tools/legalDocCreator';
import { browserUseTool } from './tools/browserUse';
import { composioActionTool } from './tools/composioAction';
import { connectAppTool } from './tools/connectApp';
import { voiceCallTool } from './tools/voiceCall';
import { rbacManagementGlobalTool } from './tools/rbacManagement';
import { inviteMemberGlobalTool } from './tools/inviteMember';
import { crossThreadSearchTool } from './tools/crossThreadSearch';

// Format-report tool imports (uses existing formatting logic)
import { runAnthropicFormatter } from '../anthropicFormatter';
import { generateDualFormatDocumentsWithStorage } from '../../utils/documentGenerator';
import { fetchCurrentTenantBranding } from '../tenantBranding';
import { getAuth } from 'firebase/auth';
import { CLAUDE_FORMATTER_DISPLAY } from '../../config/agenticWorkflowConfig';
import { isAgenticWorkflowsAuthorized } from '../agenticWorkflows';
import {
  formatResearchToHtml as formatToHtml,
  parseFormatReportPrompt,
  generateWorkflowActionButtonsHtml,
  generateSpecialMessageTemplate,
  evaluateFormattedCitationLoss,
  PREST0_PRIMARY_CTA_BG,
} from '../agenticWorkflows/helpers';
import { storeWorkflowOutput, getLatestOutputBySource, getLatestOutput, getLatestDocumentAnalysis } from './workflowOutputStore';
import { buildModelDisplay } from './modelDisplay';
import { analyzeDocumentsWithClaude, hasAnalyzableFiles as hasAnalyzableAttachments } from '../claudeDocumentAnalyzer';
import { finalizeAttachmentReadComplete } from './messaging';

import type {
  GlobalToolDefinition,
  StepContext,
  StepResult,
  ToolTheme,
  TokenUsageMetadata,
  SerializedAttachment,
} from './types';

// ============================================================================
// Core Framework Exports
// ============================================================================

export * from './types';
export * from './registry';
export * from './runtime';
export * from './messaging';

// ============================================================================
// Tool Exports
// ============================================================================

export { documentAnalyzerTool, analyzeDocuments, hasAnalyzableFiles } from './tools/documentAnalyzer';
export type { DocumentAnalyzerState, AnalyzeStepInput, AnalyzeStepOutput } from './tools/documentAnalyzer';

export {
  multiDocManifestTool,
  buildDocumentManifest,
  getManifestDocument,
  extractForManifest,
  detectLanguage,
  detectDate,
  detectParties,
  MAX_MANIFEST_FILES,
  MAX_EXTRACT_CHARS,
} from './tools/multiDocManifest';
export type { MultiDocManifestState } from './tools/multiDocManifest';

export { translateTool } from './tools/translate';
export type { TranslateState } from './tools/translate';

export { videoScriptTool } from './tools/videoScript';
export type { VideoScriptState } from './tools/videoScript';

export { deepResearchTool } from './tools/deepResearch';
export type { DeepResearchState } from './tools/deepResearch';

export { paymentGateTool } from './tools/paymentGate';
export type { PaymentGateState } from './tools/paymentGate';

export { legalDocCreatorTool } from './tools/legalDocCreator';
export type { LegalDocCreatorState } from './tools/legalDocCreator';

export { composioActionTool } from './tools/composioAction';
export type { ComposioActionState } from './tools/composioAction';

export {
  connectAppTool,
  launchConnectFlow,
  offerConnectForQuery,
  resolveRequestedApps,
  CONNECT_APP_TOOL_ID,
  CONNECT_APP_BUTTON_IDS,
} from './tools/connectApp';
export type {
  ConnectAppState,
  ConnectAppMessaging,
  ConnectLaunchOutcome,
  ResolvedConnectApp,
} from './tools/connectApp';

export { voiceCallTool } from './tools/voiceCall';
export type { VoiceCallState } from './tools/voiceCall';

export { rbacManagementGlobalTool } from './tools/rbacManagement';
export type { RbacManagementState } from './tools/rbacManagement';

// ============================================================================
// Format Report Tool (inline — wraps existing runAnthropicFormatter)
// ============================================================================

/**
 * State for the format-report tool.
 */
export interface FormatReportState {
  isFormatting: boolean;
  formattedContent?: string;
  formattedTitle?: string;
  tokenUsage?: TokenUsageMetadata;
}

const FORMAT_REPORT_THEME: ToolTheme = {
  accent: '#1e40af',     // Blue 800
  accentBg: '#dbeafe',   // Blue 100
  border: '#3b82f6',     // Blue 500
  subtitle: '#475569',   // Slate 600
  muted: '#64748b',      // Slate 500
};

const FORMAT_REPORT_BUTTON_IDS = {
  formatReport: 'global-tool-format-report-btn',
};

// Model codename display comes from the single consolidated builder
// (2026-07-06: the local buildFormatModelDisplay copy here had drifted —
// raw-vendor-id fallthrough, unversioned Opus pinned to Apex-4.7 instead of
// 4.8 — and its wrapper camouflaged the call site from buildModelDisplay
// audits, so it was removed rather than delegated).

/** Trigger pattern — reuses existing parseFormatReportPrompt from agenticWorkflows/helpers */
function matchFormatReportTrigger(text: string): { matches: boolean; inlineContent?: string } {
  const parsed = parseFormatReportPrompt(text);
  if (parsed.isFormatReport) {
    return {
      matches: true,
      inlineContent: parsed.inlineContent || undefined,
    };
  }
  return { matches: false };
}

/**
 * Format Report GlobalTool.
 *
 * Wraps the existing runAnthropicFormatter to transform content into
 * professionally formatted reports with PDF/DOCX generation.
 * Chains after deep-research or document-analyzer, before translate.
 */
export const formatReportTool: GlobalToolDefinition<FormatReportState> = {
  id: 'format-report',
  name: 'Format Report',
  description: 'Format content into a professionally structured report for a wider audience',

  triggerPattern: matchFormatReportTrigger,

  createInitialState: (): FormatReportState => ({
    isFormatting: false,
  }),

  steps: [
    // ========================================================================
    // Step 1: Validate — resolve content from chain, inline, attachments, or shared context
    // ========================================================================
    {
      id: 'validate',
      name: 'Validate Content',
      activeDisplayName: 'checking for content',

      execute: async (
        ctx: StepContext<FormatReportState>,
        rawInput?: unknown
      ): Promise<StepResult> => {
        const input = rawInput as {
          inlineContent?: string;
          content?: string;
          isChainStep?: boolean;
        } | undefined;

        let inlineContent = input?.inlineContent || input?.content || '';

        // If no inline content, try parsing from trigger message
        if (!inlineContent.trim()) {
          const parsed = parseFormatReportPrompt(ctx.triggerMessage.text);
          if (parsed.isFormatReport && parsed.inlineContent) {
            inlineContent = parsed.inlineContent;
          }
        }

        const attachments = ctx.triggerMessage.attachments;
        const hasAttachments = hasAnalyzableAttachments(attachments);

        let content: string | null = null;
        let title = 'Formatted Report';
        let timestamp = new Date();
        let source: 'research' | 'formatted' | 'document_analysis' | 'document_analysis_failed' | 'inline' | 'attachment' = 'inline';

        // ====================================================================
        // Chain step handling — use previous workflow output
        // ====================================================================
        if (input?.isChainStep) {
          console.log('[FormatReport] Chain step - checking for previous workflow output');

          const previousOutput = ctx.sharedContext.lastTranslatableContentRef?.current ||
                                 ctx.sharedContext.lastTranslatableContent;
          console.log('[FormatReport] lastTranslatableContent =', previousOutput ? `${previousOutput.source} (${previousOutput.content?.length || 0} chars)` : 'empty');

          if (previousOutput?.content?.trim()) {
            console.log('[FormatReport] Using previous workflow output as content');
            content = previousOutput.content;
            title = previousOutput.title || 'Formatted Report';
            timestamp = previousOutput.timestamp || new Date();
            source = previousOutput.source as typeof source;
          } else {
            const researchReport = ctx.sharedContext.lastResearchReportRef?.current ||
                                   ctx.sharedContext.lastResearchReport;
            console.log('[FormatReport] lastResearchReport =', researchReport ? `${researchReport.title || 'research'} (${researchReport.content?.length || 0} chars)` : 'empty');

            if (researchReport?.content?.trim()) {
              content = researchReport.content;
              title = researchReport.title || 'Formatted Report';
              timestamp = researchReport.timestamp || new Date();
              source = 'research';
              console.log('[FormatReport] Using lastResearchReport fallback');
            } else {
              const historyResearch = ctx.sharedContext.getContentBySource?.('research');
              console.log('[FormatReport] contentHistory.research =', historyResearch ? `${historyResearch.title || 'research'} (${historyResearch.content?.length || 0} chars)` : 'empty');

              if (historyResearch?.content?.trim()) {
                content = historyResearch.content;
                title = historyResearch.title || 'Formatted Report';
                timestamp = historyResearch.timestamp || new Date();
                source = 'research';
                console.log('[FormatReport] Using contentHistory research fallback');
              } else {
                const storedResearchOutput = getLatestOutputBySource('research');
                console.log('[FormatReport] workflowOutputStore.research =', storedResearchOutput ? `${storedResearchOutput.title || 'research'} (${storedResearchOutput.content?.length || 0} chars)` : 'empty');

                if (storedResearchOutput?.content?.trim()) {
                  content = storedResearchOutput.content;
                  title = storedResearchOutput.title || 'Formatted Report';
                  timestamp = storedResearchOutput.timestamp;
                  source = 'research';
                  console.log('[FormatReport] Using workflowOutputStore research fallback');
                } else {
                  const latestOutput = getLatestOutput();
                  console.log('[FormatReport] workflowOutputStore.any =', latestOutput ? `${latestOutput.source} (${latestOutput.content?.length || 0} chars)` : 'empty');

                  if (latestOutput?.content?.trim()) {
                    content = latestOutput.content;
                    title = latestOutput.title || 'Formatted Report';
                    timestamp = latestOutput.timestamp;
                    source = latestOutput.source as typeof source;
                    console.log('[FormatReport] Using latest workflow output fallback');
                  }
                }
              }
            }
          }
        }

        // ====================================================================
        // Mode A: Inline content provided after trigger phrase
        // ====================================================================
        if (!content && inlineContent.trim()) {
          content = inlineContent.trim();
          title = content.length > 100 ? content.slice(0, 100) + '...' : content;
          source = 'inline';
        }

        // ====================================================================
        // Mode B: Attachments provided with trigger
        // ====================================================================
        if (!content && hasAttachments && attachments) {
          source = 'attachment';

          // Check orchestrator cache first
          const cachedDocumentContent = ctx.sharedContext.documentContentRef?.current ||
                                        ctx.sharedContext.documentContent;
          if (cachedDocumentContent) {
            console.log('[FormatReport] Using cached document analysis from pre-orchestrator');
            content = cachedDocumentContent;
            const firstAttachment = attachments[0];
            title = firstAttachment?.name?.replace(/\.[^.]+$/, '') || 'Formatted Report';
          } else {
            // No cache — analyze now
            console.warn('[FormatReport] Cache miss - analyzing documents');
            const readStream = ctx.messaging.showPending('reading attachment');

            try {
              const analysisResult = await analyzeDocumentsWithClaude({
                files: attachments,
                userQuery: 'Extract the full text content from this document for professional formatting.',
                signal: ctx.abortSignal,
              });

              if (analysisResult.analysis && analysisResult.analysis.trim()) {
                content = analysisResult.analysis;
                const firstAttachment = attachments[0];
                title = firstAttachment?.name?.replace(/\.[^.]+$/, '') || 'Formatted Report';
                ctx.sharedContext.setDocumentContent(content);

                finalizeAttachmentReadComplete(readStream, analysisResult, ctx.recordTransaction, {
                  sourceTag: 'format_report_attachment_read',
                  transactionSummary: 'document extraction for formatting',
                });
              } else {
                readStream.finalize({
                  text: 'no content extracted from attachment',
                  html: generateSpecialMessageTemplate({
                    header: { title: 'no readable content found', icon: '⚠' },
                    sections: [{ type: 'text', content: 'the attachment could not be read. try a different file format.' }],
                    theme: 'amber',
                  }),
                  sourceTag: 'format_report_attachment_empty',
                });
                content = null;
              }
            } catch (err) {
              console.error('[FormatReport] Document analysis failed:', err);
              readStream.finalize({
                text: 'document analysis failed',
                html: generateSpecialMessageTemplate({
                  header: { title: 'document analysis failed', icon: '⚠' },
                  sections: [{ type: 'text', content: 'unable to read attachment. checking for other content sources.' }],
                  theme: 'amber',
                }),
                sourceTag: 'format_report_attachment_error',
              });
              content = null;
            }
          }
        }

        // ====================================================================
        // Mode C: No inline content, no attachments — use shared context
        // ====================================================================
        if (!content) {
          const sharedContent = ctx.sharedContext.lastTranslatableContentRef?.current ||
                               ctx.sharedContext.lastTranslatableContent;
          if (sharedContent && sharedContent.content) {
            content = sharedContent.content;
            title = sharedContent.title || 'Formatted Report';
            timestamp = sharedContent.timestamp || new Date();
            source = sharedContent.source as typeof source;
          }
        }

        // Fallback: workflowOutputStore
        if (!content) {
          const storedOutput = getLatestOutputBySource('research');
          if (storedOutput) {
            content = storedOutput.content;
            title = storedOutput.title || 'Formatted Report';
            timestamp = storedOutput.timestamp;
            source = 'research';
            console.log('[FormatReport] Using content from workflowOutputStore:', storedOutput.id);
          }
        }

        // Fallback: documentContent from pre-orchestrator analysis
        if (!content) {
          const docContent = ctx.sharedContext.documentContentRef?.current ||
                             ctx.sharedContext.documentContent;
          if (docContent && docContent.trim()) {
            content = docContent;
            title = 'Document Analysis';
            timestamp = ctx.sharedContext.documentAnalysisMeta?.analyzedAt || new Date();
            source = 'document_analysis';
            console.log('[FormatReport] Using content from sharedContext.documentContent');
          }
        }

        // Fallback: documentAnalysisStore
        if (!content) {
          const latestDocAnalysis = getLatestDocumentAnalysis();
          if (latestDocAnalysis && latestDocAnalysis.analysis) {
            content = latestDocAnalysis.analysis;
            title = 'Document Analysis';
            timestamp = latestDocAnalysis.timestamp instanceof Date ? latestDocAnalysis.timestamp : new Date(latestDocAnalysis.timestamp);
            source = 'document_analysis';
            console.log('[FormatReport] Using content from documentAnalysisStore:', latestDocAnalysis.id);
          }
        }

        // Fallback: contentHistory for research content
        if (!content) {
          const researchContent = ctx.sharedContext.getContentBySource?.('research');
          if (researchContent && researchContent.content) {
            content = researchContent.content;
            title = researchContent.title || 'Formatted Report';
            timestamp = researchContent.timestamp || new Date();
            source = 'research';
            console.log('[FormatReport] Using content from contentHistory (research)');
          }
        }

        // Final check: do we have content?
        if (!content || !content.trim()) {
          ctx.messaging.showSpecialMessage({
            header: { title: 'no content available' },
            sections: [
              {
                type: 'text',
                content: 'you can format content in three ways:',
              },
              {
                type: 'list',
                content: [
                  'run <strong>deep research</strong> first, then type <strong>"format report"</strong>',
                  'type <strong>"format report [your text here]"</strong> to format inline text',
                  'attach a document and type <strong>"format report"</strong>',
                ],
              },
            ],
            theme: 'blue',
          });

          return {
            status: 'completed',
            nextStep: null,
          };
        }

        console.log(`[FormatReport] Content resolved: source=${source}, length=${content.length}`);

        return {
          status: 'completed',
          carryForward: { content, title, timestamp, source },
        };
      },
    },

    // ========================================================================
    // Step 2: Format — run existing Claude formatter with streaming + PDF/DOCX
    // ========================================================================
    {
      id: 'format',
      name: 'Format Content',
      activeDisplayName: 'formatting report',

      execute: async (
        ctx: StepContext<FormatReportState>,
        rawInput?: unknown
      ): Promise<StepResult> => {
        const input = rawInput as {
          content: string;
          title: string;
          timestamp: Date;
          source: string;
        } | undefined;

        if (!input?.content) {
          return {
            status: 'failed',
            error: new Error('No content available for formatting'),
          };
        }

        ctx.setState({ isFormatting: true });

        const stream = ctx.messaging.showPending('formatting report');
        ctx.sharedContext.setActiveStream?.(stream);

        let formattedText = '';
        let formatterTokenUsage: TokenUsageMetadata | undefined;
        let streamFinalized = false;

        // MT-QA-BUG-001: resolve tenant firm name ONCE here; threaded into
        // the formatter prompt (so the "Facilitated by:" line is correct)
        // and into the PDF/DOCX generator below.
        const formatBranding = await fetchCurrentTenantBranding();

        try {
          const formatterResult = await runAnthropicFormatter({
            reportContent: input.content,
            firmName: formatBranding.firmName,
            signal: ctx.abortSignal,
            onChunk: (_chunk, accumulated) => {
              ctx.sharedContext.updateStreamActivity?.();
              stream.update({
                text: accumulated,
                html: formatToHtml(accumulated),
              });
            },
            onComplete: (fullText, tokenUsage) => {
              formattedText = fullText;
              formatterTokenUsage = tokenUsage;
            },
            onError: (err) => {
              stream.update({
                text: `Formatting error: ${err.message}`,
                html: `<div style="color:#dc2626;font-weight:600;">Formatting error: ${err.message}</div>`,
              });
            },
          });

          if (!formattedText.trim()) {
            stream.finalize({
              payload: {
                kind: 'tool.generic_error',
                toolId: 'format-report',
                severity: 'error',
                data: { errorMessage: 'formatter produced no output', phase: 'format' },
                summary: 'format-report: formatter returned empty content',
              },
              sourceTag: 'format_report_empty',
            });
            streamFinalized = true;
            ctx.setState({ isFormatting: false });
            return { status: 'failed', error: new Error('Formatter produced no output'), userFacingErrorShown: true };
          }

          // 2026-08-15 citation-cutoff guard (fingerprint:
          // format-report-citation-guard-v20260815): a format pass that hit its
          // token cap or dropped source URLs loses the report TAIL — the
          // References section. Never ship that; fall back to the pre-format
          // content, which still carries the complete citations.
          const citationGuard = evaluateFormattedCitationLoss(
            input.content,
            formattedText,
            formatterResult?.truncated,
          );
          if (citationGuard.useOriginal) {
            console.warn(
              '[FormatReport] format-report-citation-guard-v20260815 tripped:',
              citationGuard.reason,
              '— shipping the pre-format content instead of the formatter output',
            );
            formattedText = input.content;
            stream.update({
              text: formattedText,
              html: formatToHtml(formattedText),
            });
          }

          // Generate PDF and DOCX with Firebase Storage upload
          const auth = getAuth();
          const currentUid = auth.currentUser?.uid;
          // Reuse the branding resolved above (MT-QA-BUG-001).
          const formatTenantBranding = formatBranding;

          let pdf: File | undefined;
          let docx: File | undefined;
          let finalPdfSerialized: SerializedAttachment | undefined;
          let finalDocxSerialized: SerializedAttachment | undefined;

          try {
            const {
              pdf: generatedPdf,
              docx: generatedDocx,
              pdfSerialized,
              docxSerialized,
              pdfSerializedWithStorage,
              docxSerializedWithStorage,
              uploadStatus,
            } = await generateDualFormatDocumentsWithStorage(
              {
                type: 'formatted',
                content: formattedText,
                title: input.title,
                createdAt: input.timestamp,
                translations: { organizationName: formatTenantBranding.firmName },
              },
              currentUid ? { uid: currentUid, messageId: `fr-${Date.now()}` } : undefined
            );

            pdf = generatedPdf;
            docx = generatedDocx;
            finalPdfSerialized = pdfSerializedWithStorage || pdfSerialized;
            finalDocxSerialized = docxSerializedWithStorage || docxSerialized;

            if (uploadStatus?.error) {
              console.warn('[FormatReport] Storage upload issue:', uploadStatus.error);
            }
          } catch (docGenError) {
            console.error('[FormatReport] Document generation failed:', docGenError);
            // Continue without PDF/DOCX — formatted content is still valuable
          }

          // Build lineage tokenUsages: upstream research usages + this formatting step
          const upstreamUsages = ctx.sharedContext.lastTranslatableContentRef?.current?.tokenUsages || [];
          const formatLineageUsages: TokenUsageMetadata[] = [
            ...upstreamUsages,
            ...(formatterTokenUsage ? [{ ...formatterTokenUsage, summary: 'formatted report' }] : []),
          ];

          // Store in workflowOutputStore for downstream workflows
          const storedOutputId = storeWorkflowOutput({
            content: formattedText,
            title: input.title,
            timestamp: new Date(),
            source: 'formatted',
            tokenUsages: formatLineageUsages.length > 0 ? formatLineageUsages : undefined,
          });

          // Set shared context for downstream workflows (both state AND ref for sync chain timing)
          const translatableData = {
            content: formattedText,
            title: input.title,
            timestamp: new Date(),
            source: 'formatted' as const,
            tokenUsages: formatLineageUsages.length > 0 ? formatLineageUsages : undefined,
          };
          ctx.sharedContext.setLastTranslatableContent(translatableData);
          ctx.sharedContext.lastTranslatableContentRef.current = translatableData;
          ctx.sharedContext.pushContentHistory?.(translatableData);

          // Follow-up action: platform CTA tokens only (2026-08-22 owner
          // directive — special-element buttons ride the warm ink/gold scheme,
          // and the deep-research / write-video-script pair no longer attaches
          // to special elements at all).
          const actionButtonsHtml = generateWorkflowActionButtonsHtml([
            { label: 'translate', id: TRANSLATE_BUTTON_IDS.translate, background: PREST0_PRIMARY_CTA_BG },
          ], { theme: 'blue' });

          // Build attachments arrays
          const attachments: File[] = [];
          const attachmentsSerialized: SerializedAttachment[] = [];
          if (pdf) attachments.push(pdf);
          if (docx) attachments.push(docx);
          if (finalPdfSerialized) attachmentsSerialized.push(finalPdfSerialized);
          if (finalDocxSerialized) attachmentsSerialized.push(finalDocxSerialized);

          // Finalize streaming message
          stream.finalize({
            text: formattedText,
            html: formatToHtml(formattedText),
            sourceTag: 'format_report_result',
            attachments: attachments.length > 0 ? attachments : undefined,
            attachmentsSerialized: attachmentsSerialized.length > 0 ? attachmentsSerialized : undefined,
            tokenUsages: formatLineageUsages.length > 0 ? formatLineageUsages : undefined,
            tokenUsage: formatterTokenUsage ? { ...formatterTokenUsage, summary: 'formatted report' } : undefined,
            workflowOutput: {
              type: 'formatted_content',
              content: formattedText,
              title: input.title,
              timestamp: new Date(),
              sourceWorkflow: 'format-report',
            },
            workflowActionButtonsHtml: actionButtonsHtml,
          });
          streamFinalized = true;

          // Record transaction AFTER finalize (fixes timing issue)
          if (formatterTokenUsage) {
            ctx.recordTransaction({
              tokenUsage: formatterTokenUsage,
              source: 'agenticWorkflows',
              modelOverride: buildModelDisplay(formatterTokenUsage, 'Formatting', CLAUDE_FORMATTER_DISPLAY),
              summary: 'formatted report for wider audience',
            });
          }

          ctx.setState({
            isFormatting: false,
            formattedContent: formattedText,
            formattedTitle: input.title,
            tokenUsage: formatterTokenUsage,
          });

          return {
            status: 'completed',
            output: {
              formattedContent: formattedText,
              formattedTitle: input.title,
              tokenUsage: formatterTokenUsage,
            },
          };
        } catch (error) {
          ctx.setState({ isFormatting: false });
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';

          stream.finalize({
            payload: {
              kind: 'tool.generic_error',
              toolId: 'format-report',
              severity: 'error',
              data: { errorMessage, phase: 'format' },
              summary: `format-report: failed — ${errorMessage}`,
            },
            sourceTag: 'format_report_error',
          });
          streamFinalized = true;

          return {
            status: 'failed',
            error: new Error(`Unable to format content: ${errorMessage}`),
            userFacingErrorShown: true, // stream.finalize already showed the error
          };
        } finally {
          // EMERGENCY SAFETY NET: Ensure stream is always finalized
          if (!streamFinalized) {
            console.error('[FormatReport] EMERGENCY: Stream not finalized - forcing fallback');
            try {
              stream.finalize({
                payload: {
                  kind: 'tool.generic_error',
                  toolId: 'format-report',
                  severity: 'error',
                  data: { errorMessage: 'formatting encountered an unexpected issue', phase: 'emergency_fallback' },
                  summary: 'format-report: emergency fallback fired',
                },
                sourceTag: 'format_report_emergency_fallback',
              });
            } catch (emergencyError) {
              console.error('[FormatReport] Even emergency finalization failed:', emergencyError);
            }
          }

          ctx.setState({ isFormatting: false });
          ctx.sharedContext.setActiveStream?.(null);
        }
      },

      cleanup: async (ctx: StepContext<FormatReportState>) => {
        ctx.setState({ isFormatting: false });
      },
    },
  ],

  isAuthorized: isAgenticWorkflowsAuthorized,

  theme: FORMAT_REPORT_THEME,

  buttonIds: FORMAT_REPORT_BUTTON_IDS,

  canTriggerFromButton: true,
};

// ============================================================================
// Tool Registration
// ============================================================================

/**
 * Register all built-in tools with the global registry.
 * Call this once during app initialization.
 */
export function registerAllTools(): void {
  registerTool(documentAnalyzerTool);
  registerTool(multiDocManifestTool);
  registerTool(translateTool);
  registerTool(videoScriptTool);
  registerTool(deepResearchTool);
  registerTool(paymentGateTool);
  registerTool(formatReportTool);
  registerTool(legalDocCreatorTool);
  registerTool(browserUseTool);
  registerTool(composioActionTool);
  registerTool(connectAppTool);
  registerTool(voiceCallTool);
  registerTool(rbacManagementGlobalTool);
  registerTool(inviteMemberGlobalTool);
  registerTool(crossThreadSearchTool);

  // (D13-6b: runRegistryValidation was removed with the orchestrator
  // directory — it cross-checked the DELETED brain's workflow-routing table
  // against live tools, a check with no subject left.)
}

/**
 * All available tools as an array.
 * Useful for iteration or dynamic tool selection.
 */
export const ALL_TOOLS = [
  documentAnalyzerTool,
  multiDocManifestTool,
  crossThreadSearchTool,
  translateTool,
  videoScriptTool,
  deepResearchTool,
  paymentGateTool,
  formatReportTool,
  legalDocCreatorTool,
] as const;

// ============================================================================
// Hook Export
// ============================================================================

export { useGlobalTools } from './hooks/useGlobalTools';
export type { UseGlobalToolsReturn, GlobalToolsHook } from './hooks/useGlobalTools';

// ============================================================================
// Convenience Re-exports from Messaging
// ============================================================================

export { createMessagingHelpers, formatResearchToHtml, createStyledHtml, generateButtonHtml } from './messaging';
